import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  FileText,
  Plus,
  Trash2,
  Copy,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  CheckCircle2,
  Edit3,
} from 'lucide-react'
import { sportsService } from './sportsService'
import {
  PracticeScript,
  PlannedRep,
  LineupTemplate,
  OffensivePlay,
  DefensiveCall,
  RosterAthleteOption,
  LineupSlot,
  ScriptMode,
  ScriptStatus,
  ScriptConflict,
} from '../../types/sports'
import { LineupEditorModal } from './LineupEditorModal'
import { Modal } from '../shared/Modal'

interface ScriptEditorModalProps {
  isOpen: boolean
  onClose: () => void
  scriptId?: string | null
  sessionId?: string | null
  sessionTitle?: string | null
  blockId?: string | null
  blockTitle?: string | null
  eventTitle?: string | null
  teamId: string
  onSaved: () => void
}

export const ScriptEditorModal: React.FC<ScriptEditorModalProps> = ({
  isOpen,
  onClose,
  scriptId,
  sessionId,
  sessionTitle,
  blockId,
  blockTitle,
  eventTitle,
  teamId,
  onSaved,
}) => {
  // Estado del guion
  const [name, setName] = useState('')
  const [objective, setObjective] = useState('')
  const [mode, setMode] = useState<ScriptMode>('guided_practice')
  const [internalNotes, setInternalNotes] = useState('')
  const [status, setStatus] = useState<ScriptStatus>('draft')
  const [reps, setReps] = useState<PlannedRep[]>([])

  // Catálogos auxiliares
  const [lineupTemplates, setLineupTemplates] = useState<LineupTemplate[]>([])
  const [offensivePlays, setOffensivePlays] = useState<OffensivePlay[]>([])
  const [defensiveCalls, setDefensiveCalls] = useState<DefensiveCall[]>([])
  const [rosterAthletes, setRosterAthletes] = useState<RosterAthleteOption[]>([])

  // Estado de edición / UI
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [conflicts, setConflicts] = useState<ScriptConflict[]>([])
  const initialSnapshotRef = useRef<string>('')

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false
    const current = JSON.stringify({
      name,
      objective,
      mode,
      internalNotes,
      status,
      repsCount: reps.length,
    })
    return current !== initialSnapshotRef.current
  }, [name, objective, mode, internalNotes, status, reps.length])

  // Selección múltiple para acciones en lote
  const [selectedRepIndices, setSelectedRepIndices] = useState<number[]>([])

  // Submodal para editar quinteto específico de una repetición
  const [lineupModalState, setLineupModalState] = useState<{
    isOpen: boolean
    repIndex: number
    unit: 'offense' | 'defense'
    initialName: string
    initialSlots: LineupSlot[]
    sessionKey?: number
  }>({
    isOpen: false,
    repIndex: -1,
    unit: 'offense',
    initialName: '',
    initialSlots: [],
    sessionKey: 0,
  })

  // Carga inicial de datos
  useEffect(() => {
    if (!isOpen) return

    const loadData = async () => {
      setIsLoading(true)
      setErrorMsg(null)
      try {
        const [templates, plays, calls, athletes] = await Promise.all([
          sportsService.getLineupTemplates(teamId, { status: 'active' }),
          sportsService.getOffensivePlays(teamId, { status: 'active' }),
          sportsService.getDefensiveCalls(teamId, { status: 'active' }),
          sportsService.getTeamAthletesWithEventContext(teamId),
        ])

        setLineupTemplates(templates)
        setOffensivePlays(plays)
        setDefensiveCalls(calls)
        setRosterAthletes(athletes)

        if (scriptId) {
          const script = await sportsService.getPracticeScriptById(scriptId)
          setName(script.name)
          setObjective(script.objective || '')
          setMode(script.mode)
          setInternalNotes(script.internal_notes || '')
          setStatus(script.status)
          setReps(script.reps || [])
          initialSnapshotRef.current = JSON.stringify({
            name: script.name,
            objective: script.objective || '',
            mode: script.mode,
            internalNotes: script.internal_notes || '',
            status: script.status,
            repsCount: (script.reps || []).length,
          })
        } else {
          // Valores por defecto para nuevo guion
          setName(blockTitle ? `Guion Team: ${blockTitle}` : 'Nuevo Guion de Práctica')
          setObjective('')
          setMode('guided_practice')
          setInternalNotes('')
          setStatus('draft')

          // Crear repetición inicial propuesta con las plantillas base
          const defaultOffTemplate = templates.find((t) => t.unit === 'offense')
          const defaultDefTemplate = templates.find((t) => t.unit === 'defense')

          const initialRep: PlannedRep = {
            id: crypto.randomUUID(),
            script_id: '',
            team_id: teamId,
            order_index: 1,
            offense_lineup_template_id: defaultOffTemplate?.id || null,
            offense_lineup_name: defaultOffTemplate?.name || 'Ataque 1',
            offense_lineup_is_custom: false,
            offense_slots: defaultOffTemplate
              ? defaultOffTemplate.slots.map((s) => ({
                  athlete_id: s.athlete_id,
                  position: s.position,
                  athlete_name: s.athlete
                    ? `${s.athlete.first_name} ${s.athlete.last_name}`
                    : 'Atleta',
                  dorsal_number: s.athlete?.dorsal_number,
                  primary_position: s.athlete?.primary_position,
                }))
              : [],
            offense_concept_id: null,
            offense_concept_name: 'Libre / no prefijada',
            offense_play_id: null,
            offense_play_name: 'Libre / no prefijada',
            offense_play_code: 'LIBRE',
            offense_adjustments: null,
            defense_lineup_template_id: defaultDefTemplate?.id || null,
            defense_lineup_name: defaultDefTemplate?.name || 'Defensa A',
            defense_lineup_is_custom: false,
            defense_slots: defaultDefTemplate
              ? defaultDefTemplate.slots.map((s) => ({
                  athlete_id: s.athlete_id,
                  position: s.position,
                  athlete_name: s.athlete
                    ? `${s.athlete.first_name} ${s.athlete.last_name}`
                    : 'Atleta',
                  dorsal_number: s.athlete?.dorsal_number,
                  primary_position: s.athlete?.primary_position,
                }))
              : [],
            defense_call_id: null,
            defense_call_name: 'Libre / no prefijada',
            defense_call_code: 'LIBRE',
            defense_adjustments: null,
            initial_situation: null,
            staff_notes: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }

          setReps([initialRep])
          initialSnapshotRef.current = JSON.stringify({
            name: blockTitle ? `Guion Team: ${blockTitle}` : 'Nuevo Guion de Práctica',
            objective: '',
            mode: 'guided_practice',
            internalNotes: '',
            status: 'draft',
            repsCount: 1,
          })
        }
      } catch (err: unknown) {
        const e = err as Error
        setErrorMsg(e.message || 'Error al cargar datos del guion')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [isOpen, scriptId, teamId, blockTitle])

  // Recalcular conflictos al cambiar las repeticiones
  useEffect(() => {
    const foundConflicts = sportsService.detectScriptConflicts(reps)
    setConflicts(foundConflicts)
  }, [reps])

  if (!isOpen) return null

  // --------------------------------------------------------------------------
  // MANIPULACIÓN DE REPETICIONES
  // --------------------------------------------------------------------------

  const handleAddRep = () => {
    const lastRep = reps[reps.length - 1]
    const nextOrder = reps.length + 1

    const newRep: PlannedRep = {
      id: crypto.randomUUID(),
      script_id: scriptId || '',
      team_id: teamId,
      order_index: nextOrder,
      offense_lineup_template_id: lastRep?.offense_lineup_template_id || null,
      offense_lineup_name: lastRep?.offense_lineup_name || 'Ataque 1',
      offense_lineup_is_custom: lastRep?.offense_lineup_is_custom || false,
      offense_slots: lastRep ? [...lastRep.offense_slots] : [],
      offense_concept_id: lastRep?.offense_concept_id || null,
      offense_concept_name: lastRep?.offense_concept_name || 'Libre / no prefijada',
      offense_play_id: lastRep?.offense_play_id || null,
      offense_play_name: lastRep?.offense_play_name || 'Libre / no prefijada',
      offense_play_code: lastRep?.offense_play_code || 'LIBRE',
      offense_adjustments: null,
      defense_lineup_template_id: lastRep?.defense_lineup_template_id || null,
      defense_lineup_name: lastRep?.defense_lineup_name || 'Defensa A',
      defense_lineup_is_custom: lastRep?.defense_lineup_is_custom || false,
      defense_slots: lastRep ? [...lastRep.defense_slots] : [],
      defense_call_id: lastRep?.defense_call_id || null,
      defense_call_name: lastRep?.defense_call_name || 'Libre / no prefijada',
      defense_call_code: lastRep?.defense_call_code || 'LIBRE',
      defense_adjustments: null,
      initial_situation: lastRep?.initial_situation || null,
      staff_notes: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    setReps([...reps, newRep])
  }

  const handleDuplicateRep = (index: number) => {
    const target = reps[index]
    if (!target) return

    const clone: PlannedRep = {
      ...target,
      id: crypto.randomUUID(),
      offense_slots: [...target.offense_slots],
      defense_slots: [...target.defense_slots],
      order_index: target.order_index + 1,
    }

    const updated = [...reps]
    updated.splice(index + 1, 0, clone)
    // Reindexar orden
    updated.forEach((r, idx) => {
      r.order_index = idx + 1
    })
    setReps(updated)
  }

  const handleDeleteRep = (index: number) => {
    if (reps.length <= 1) {
      setErrorMsg('El guion debe conservar al menos una repetición.')
      return
    }
    const updated = reps.filter((_, idx) => idx !== index)
    updated.forEach((r, idx) => {
      r.order_index = idx + 1
    })
    setReps(updated)
    setSelectedRepIndices((prev) => prev.filter((i) => i !== index))
  }

  const handleMoveRep = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === reps.length - 1) return

    const targetIndex = direction === 'up' ? index - 1 : index + 1
    const updated = [...reps]
    const temp = updated[index]
    updated[index] = updated[targetIndex]
    updated[targetIndex] = temp

    updated.forEach((r, idx) => {
      r.order_index = idx + 1
    })
    setReps(updated)
  }

  // --------------------------------------------------------------------------
  // CAMBIO RÁPIDO DE QUINTETOS Y JUGADAS POR REP
  // --------------------------------------------------------------------------

  const handleSelectLineupTemplate = (
    index: number,
    unit: 'offense' | 'defense',
    templateIdStr: string
  ) => {
    const template = lineupTemplates.find((t) => t.id === templateIdStr)
    if (!template) return

    const compiledSlots: LineupSlot[] = template.slots.map((s) => ({
      athlete_id: s.athlete_id,
      position: s.position,
      athlete_name: s.athlete ? `${s.athlete.first_name} ${s.athlete.last_name}` : 'Atleta',
      dorsal_number: s.athlete?.dorsal_number,
      primary_position: s.athlete?.primary_position,
    }))

    const updated = [...reps]
    if (unit === 'offense') {
      updated[index] = {
        ...updated[index],
        offense_lineup_template_id: template.id,
        offense_lineup_name: template.name,
        offense_lineup_is_custom: false,
        offense_slots: compiledSlots,
      }
    } else {
      updated[index] = {
        ...updated[index],
        defense_lineup_template_id: template.id,
        defense_lineup_name: template.name,
        defense_lineup_is_custom: false,
        defense_slots: compiledSlots,
      }
    }
    setReps(updated)
  }

  const handleSelectOffensePlay = (index: number, playIdStr: string) => {
    const updated = [...reps]
    if (!playIdStr || playIdStr === 'LIBRE') {
      updated[index] = {
        ...updated[index],
        offense_concept_id: null,
        offense_concept_name: 'Libre / no prefijada',
        offense_play_id: null,
        offense_play_name: 'Libre / no prefijada',
        offense_play_code: 'LIBRE',
      }
    } else {
      const play = offensivePlays.find((p) => p.id === playIdStr)
      if (play) {
        updated[index] = {
          ...updated[index],
          offense_concept_id: play.concept_id,
          offense_concept_name: play.concept?.name || 'Concepto',
          offense_play_id: play.id,
          offense_play_name: play.name,
          offense_play_code: play.short_code,
        }
      }
    }
    setReps(updated)
  }

  const handleSelectDefenseCall = (index: number, callIdStr: string) => {
    const updated = [...reps]
    if (!callIdStr || callIdStr === 'LIBRE') {
      updated[index] = {
        ...updated[index],
        defense_call_id: null,
        defense_call_name: 'Libre / no prefijada',
        defense_call_code: 'LIBRE',
      }
    } else {
      const call = defensiveCalls.find((c) => c.id === callIdStr)
      if (call) {
        updated[index] = {
          ...updated[index],
          defense_call_id: call.id,
          defense_call_name: call.name,
          defense_call_code: call.short_code,
        }
      }
    }
    setReps(updated)
  }

  // --------------------------------------------------------------------------
  // EDICIÓN EN LOTE (BULK ACTIONS)
  // --------------------------------------------------------------------------

  const toggleSelectRep = (index: number) => {
    setSelectedRepIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    )
  }

  const handleSelectAllReps = () => {
    if (selectedRepIndices.length === reps.length) {
      setSelectedRepIndices([])
    } else {
      setSelectedRepIndices(reps.map((_, idx) => idx))
    }
  }

  const handleApplyBulkLineup = (unit: 'offense' | 'defense', templateIdStr: string) => {
    const template = lineupTemplates.find((t) => t.id === templateIdStr)
    if (!template) return

    const compiledSlots: LineupSlot[] = template.slots.map((s) => ({
      athlete_id: s.athlete_id,
      position: s.position,
      athlete_name: s.athlete ? `${s.athlete.first_name} ${s.athlete.last_name}` : 'Atleta',
      dorsal_number: s.athlete?.dorsal_number,
      primary_position: s.athlete?.primary_position,
    }))

    const updated = [...reps]
    selectedRepIndices.forEach((idx) => {
      if (unit === 'offense') {
        updated[idx] = {
          ...updated[idx],
          offense_lineup_template_id: template.id,
          offense_lineup_name: template.name,
          offense_lineup_is_custom: false,
          offense_slots: compiledSlots,
        }
      } else {
        updated[idx] = {
          ...updated[idx],
          defense_lineup_template_id: template.id,
          defense_lineup_name: template.name,
          defense_lineup_is_custom: false,
          defense_slots: compiledSlots,
        }
      }
    })
    setReps(updated)
  }

  const handleApplyBulkOffensePlay = (playIdStr: string) => {
    const updated = [...reps]
    selectedRepIndices.forEach((idx) => {
      if (!playIdStr || playIdStr === 'LIBRE') {
        updated[idx] = {
          ...updated[idx],
          offense_concept_id: null,
          offense_concept_name: 'Libre / no prefijada',
          offense_play_id: null,
          offense_play_name: 'Libre / no prefijada',
          offense_play_code: 'LIBRE',
        }
      } else {
        const play = offensivePlays.find((p) => p.id === playIdStr)
        if (play) {
          updated[idx] = {
            ...updated[idx],
            offense_concept_id: play.concept_id,
            offense_concept_name: play.concept?.name || 'Concepto',
            offense_play_id: play.id,
            offense_play_name: play.name,
            offense_play_code: play.short_code,
          }
        }
      }
    })
    setReps(updated)
  }

  const handleApplyBulkDefenseCall = (callIdStr: string) => {
    const updated = [...reps]
    selectedRepIndices.forEach((idx) => {
      if (!callIdStr || callIdStr === 'LIBRE') {
        updated[idx] = {
          ...updated[idx],
          defense_call_id: null,
          defense_call_name: 'Libre / no prefijada',
          defense_call_code: 'LIBRE',
        }
      } else {
        const call = defensiveCalls.find((c) => c.id === callIdStr)
        if (call) {
          updated[idx] = {
            ...updated[idx],
            defense_call_id: call.id,
            defense_call_name: call.name,
            defense_call_code: call.short_code,
          }
        }
      }
    })
    setReps(updated)
  }

  // --------------------------------------------------------------------------
  // GUARDADO ATÓMICO Y VALIDACIONES
  // --------------------------------------------------------------------------

  const handleSaveScript = async (targetStatus?: ScriptStatus) => {
    const statusToSave = targetStatus || status
    setErrorMsg(null)
    setSuccessMsg(null)

    if (!name.trim()) {
      setErrorMsg('El nombre del guion es obligatorio.')
      return
    }

    if (reps.length === 0) {
      setErrorMsg('El guion debe incluir al menos una repetición planificada.')
      return
    }

    // Si se intenta marcar como 'ready', validar conflictos estrictamente
    if (statusToSave === 'ready') {
      const activeConflicts = sportsService.detectScriptConflicts(reps)
      if (activeConflicts.length > 0) {
        setErrorMsg(
          `No se puede marcar como "Listo": existen ${activeConflicts.length} conflicto(s) de jugador simultáneo en ataque y defensa. Revisa las repeticiones señaladas.`
        )
        return
      }

      // Validar que todas las repeticiones tengan 5 jugadores en ataque y 5 en defensa
      for (let i = 0; i < reps.length; i++) {
        const r = reps[i]
        if ((r.offense_slots || []).length !== 5) {
          setErrorMsg(`La Rep #${r.order_index} no tiene los 5 jugadores asignados en ataque.`)
          return
        }
        if ((r.defense_slots || []).length !== 5) {
          setErrorMsg(`La Rep #${r.order_index} no tiene los 5 jugadores asignados en defensa.`)
          return
        }
      }
    }

    setIsSaving(true)
    try {
      const scriptPayload: Partial<PracticeScript> & { team_id: string; name: string } = {
        id: scriptId || undefined,
        team_id: teamId,
        session_id: sessionId || null,
        block_id: blockId || null,
        name: name.trim(),
        objective: objective.trim() || null,
        mode,
        internal_notes: internalNotes.trim() || null,
        status: statusToSave,
      }

      await sportsService.savePracticeScript(scriptPayload, reps)
      setStatus(statusToSave)
      setSuccessMsg(`Guion guardado exitosamente como ${statusToSave === 'ready' ? 'Listo' : 'Borrador'}.`)
      onSaved()
      setTimeout(() => {
        onClose()
      }, 700)
    } catch (err: unknown) {
      const e = err as Error
      setErrorMsg(e.message || 'Error al guardar el guion')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDuplicateEntireScript = async () => {
    if (!scriptId) return
    setIsSaving(true)
    try {
      await sportsService.duplicatePracticeScript(
        scriptId,
        `${name} (Copia)`,
        sessionId,
        blockId
      )
      setSuccessMsg('Guion duplicado exitosamente.')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const e = err as Error
      setErrorMsg(e.message || 'Error al duplicar el guion')
    } finally {
      setIsSaving(false)
    }
  }

  const subtitleNode = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap', fontSize: '0.8rem' }}>
      {eventTitle && (
        <>
          <span style={{ color: 'var(--text-secondary)' }}>Concentración: <strong>{eventTitle}</strong></span>
          <span style={{ color: 'var(--text-muted)' }}>/</span>
        </>
      )}
      {sessionTitle && (
        <>
          <span style={{ color: 'var(--text-secondary)' }}>Sesión: <strong>{sessionTitle}</strong></span>
          <span style={{ color: 'var(--text-muted)' }}>/</span>
        </>
      )}
      {blockTitle && (
        <span style={{ color: 'var(--brand-red)', fontWeight: 600 }}>Bloque: {blockTitle}</span>
      )}
      {!eventTitle && !sessionTitle && !blockTitle && (
        <span style={{ color: 'var(--text-secondary)' }}>Sesión independiente</span>
      )}
    </div>
  )

  const footerNode = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        flexWrap: 'wrap',
        gap: '0.65rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {scriptId && (
          <button
            type="button"
            onClick={handleDuplicateEntireScript}
            disabled={isSaving}
            className="btn btn-outline"
            style={{ fontSize: '0.82rem' }}
          >
            <Copy size={14} style={{ marginRight: '0.25rem' }} /> Duplicar Guion
          </button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-outline"
          style={{ fontSize: '0.85rem' }}
        >
          Cancelar
        </button>

        <button
          type="button"
          onClick={() => handleSaveScript('draft')}
          disabled={isSaving}
          className="btn btn-outline"
          style={{ fontSize: '0.85rem' }}
        >
          <FileText size={15} style={{ marginRight: '0.35rem' }} />
          Guardar Borrador
        </button>

        <button
          type="button"
          onClick={() => handleSaveScript('ready')}
          disabled={isSaving || conflicts.length > 0}
          className="btn btn-primary"
          style={{
            fontSize: '0.85rem',
            backgroundColor: conflicts.length > 0 ? '#9ca3af' : undefined,
            cursor: conflicts.length > 0 ? 'not-allowed' : 'pointer',
          }}
          title={conflicts.length > 0 ? 'Resuelve los conflictos antes de marcar Listo' : 'Marcar como listo'}
        >
          <CheckCircle2 size={15} style={{ marginRight: '0.35rem' }} />
          {isSaving ? 'Guardando...' : 'Marcar como Listo'}
        </button>
      </div>
    </div>
  )

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        zIndex={1050}
        hasUnsavedChanges={isDirty}
        icon={<FileText size={20} />}
        title={name.trim() || 'Planificación Guion Team'}
        subtitle={subtitleNode}
        badge={
          <span className={`badge ${status === 'ready' ? 'badge-green' : 'badge-yellow'}`}>
            {status === 'ready' ? 'Listo' : 'Borrador'}
          </span>
        }
        footer={footerNode}
      >
        {/* Encabezado y Metadatos del Guion */}
        <div
          style={{
            padding: '1rem 1.25rem',
            marginBottom: '1rem',
            border: '1px solid var(--surface-border)',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--surface-subtle)',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start' }}>
            {/* Nombre y Modo */}
            <div style={{ flex: '1 1 300px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Nombre del Guion
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej. Guion Team Semana 1"
                style={{
                  width: '100%',
                  padding: '0.45rem 0.65rem',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--surface-border)',
                  backgroundColor: 'var(--surface-input)',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              />
            </div>

            {/* Modo de Trabajo */}
            <div style={{ width: '180px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Modo de Trabajo
              </label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as ScriptMode)}
                style={{
                  width: '100%',
                  padding: '0.45rem 0.65rem',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--surface-border)',
                  backgroundColor: 'var(--surface-input)',
                  fontSize: '0.85rem',
                }}
              >
                <option value="guided_practice">Práctica Guiada</option>
                <option value="competitive_team">Team Competitivo</option>
              </select>
            </div>

            {/* Estado del Guion */}
            <div style={{ width: '140px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Estado
              </label>
              <span
                className={`badge ${status === 'ready' ? 'badge-green' : 'badge-yellow'}`}
                style={{ display: 'inline-flex', padding: '0.45rem 0.75rem', fontSize: '0.8rem' }}
              >
                {status === 'ready' ? 'Listo para sesión' : 'Borrador'}
              </span>
            </div>

            {/* Objetivo y Notas en línea plegable o inputs */}
            <div style={{ flex: '1 1 100%', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 400px' }}>
                <input
                  type="text"
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  placeholder="Objetivo pedagógico / técnico del bloque..."
                  style={{
                    width: '100%',
                    padding: '0.35rem 0.6rem',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--surface-border)',
                    fontSize: '0.82rem',
                  }}
                />
              </div>
              <div style={{ flex: '1 1 400px' }}>
                <input
                  type="text"
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Notas internas exclusivas de staff (privadas)..."
                  style={{
                    width: '100%',
                    padding: '0.35rem 0.6rem',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--surface-border)',
                    fontSize: '0.82rem',
                    color: 'var(--text-secondary)',
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mensajes de Alerta y Conflictos */}
        {errorMsg && (
          <div
            style={{
              padding: '0.65rem 1.25rem',
              backgroundColor: 'rgba(217, 35, 44, 0.08)',
              borderBottom: '1px solid rgba(217, 35, 44, 0.2)',
              color: 'var(--brand-red)',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <AlertTriangle size={16} style={{ flexShrink: 0 }} />
            <span>{errorMsg}</span>
          </div>
        )}

        {successMsg && (
          <div
            style={{
              padding: '0.65rem 1.25rem',
              backgroundColor: 'rgba(16, 185, 129, 0.08)',
              borderBottom: '1px solid rgba(16, 185, 129, 0.2)',
              color: '#059669',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
            <span>{successMsg}</span>
          </div>
        )}

        {conflicts.length > 0 && (
          <div
            style={{
              padding: '0.65rem 1.25rem',
              backgroundColor: 'rgba(217, 35, 44, 0.1)',
              borderBottom: '1px solid var(--brand-red)',
              color: 'var(--brand-red)',
              fontSize: '0.82rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.2rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <AlertTriangle size={15} /> CONFLICTOS DE ALINEACIÓN DETECTADOS ({conflicts.length}):
            </div>
            <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
              {conflicts.map((c, idx) => (
                <li key={idx}>{c.description}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Barra de Acciones en Lote (si hay selecciones) */}
        {selectedRepIndices.length > 0 && (
          <div
            style={{
              padding: '0.6rem 1.25rem',
              backgroundColor: 'rgba(15, 23, 42, 0.08)',
              borderBottom: '1px solid var(--surface-border)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flexWrap: 'wrap',
              fontSize: '0.82rem',
            }}
          >
            <span style={{ fontWeight: 600 }}>
              {selectedRepIndices.length} repetición(es) seleccionadas:
            </span>

            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleApplyBulkLineup('offense', e.target.value)
                  e.target.value = ''
                }
              }}
              style={{ padding: '0.3rem 0.5rem', fontSize: '0.8rem', borderRadius: 'var(--radius-sm)' }}
            >
              <option value="">-- Asignar Quinteto Ataque --</option>
              {lineupTemplates
                .filter((t) => t.unit === 'offense')
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>

            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleApplyBulkOffensePlay(e.target.value)
                  e.target.value = ''
                }
              }}
              style={{ padding: '0.3rem 0.5rem', fontSize: '0.8rem', borderRadius: 'var(--radius-sm)' }}
            >
              <option value="">-- Asignar Jugada Ataque --</option>
              <option value="LIBRE">Libre / no prefijada</option>
              {offensivePlays.map((p) => (
                <option key={p.id} value={p.id}>
                  [{p.short_code}] {p.name}
                </option>
              ))}
            </select>

            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleApplyBulkLineup('defense', e.target.value)
                  e.target.value = ''
                }
              }}
              style={{ padding: '0.3rem 0.5rem', fontSize: '0.8rem', borderRadius: 'var(--radius-sm)' }}
            >
              <option value="">-- Asignar Quinteto Defensa --</option>
              {lineupTemplates
                .filter((t) => t.unit === 'defense')
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>

            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleApplyBulkDefenseCall(e.target.value)
                  e.target.value = ''
                }
              }}
              style={{ padding: '0.3rem 0.5rem', fontSize: '0.8rem', borderRadius: 'var(--radius-sm)' }}
            >
              <option value="">-- Asignar Llamada Defensa --</option>
              <option value="LIBRE">Libre / no prefijada</option>
              {defensiveCalls.map((c) => (
                <option key={c.id} value={c.id}>
                  [{c.short_code}] {c.name}
                </option>
              ))}
            </select>

            <button
              onClick={() => setSelectedRepIndices([])}
              className="btn btn-outline"
              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', marginLeft: 'auto' }}
            >
              Deseleccionar
            </button>
          </div>
        )}

        {/* Listado de Repeticiones */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
              Cargando planificación...
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Barra superior de listado */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={selectedRepIndices.length === reps.length && reps.length > 0}
                    onChange={handleSelectAllReps}
                    aria-label="Seleccionar todas las repeticiones"
                  />
                  <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    Secuencia Prevista ({reps.length} repeticiones)
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleAddRep}
                  className="btn btn-outline"
                  style={{ fontSize: '0.8rem', padding: '0.35rem 0.65rem' }}
                >
                  <Plus size={14} style={{ marginRight: '0.25rem' }} /> Añadir Repetición
                </button>
              </div>

              {/* Tarjetas / Filas de Repeticiones */}
              {reps.map((rep, idx) => {
                const isSelected = selectedRepIndices.includes(idx)
                const hasRepConflict = conflicts.some((c) => c.repIndex === rep.order_index)

                return (
                  <div
                    key={rep.id || idx}
                    style={{
                      border: hasRepConflict
                        ? '1px solid var(--brand-red)'
                        : isSelected
                        ? '1px solid #2563eb'
                        : '1px solid var(--surface-border)',
                      borderRadius: 'var(--radius-md)',
                      backgroundColor: hasRepConflict
                        ? 'rgba(217, 35, 44, 0.04)'
                        : 'var(--surface-card)',
                      padding: '0.75rem 1rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.6rem',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    }}
                  >
                    {/* Fila Principal de la Repetición */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {/* Checkbox y Orden */}
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectRep(idx)}
                        aria-label={`Seleccionar repetición ${rep.order_index}`}
                      />

                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: 'var(--radius-md)',
                          backgroundColor: 'var(--surface-secondary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          color: 'var(--text-primary)',
                        }}
                      >
                        #{rep.order_index}
                      </div>

                      {/* Botones de subida y bajada móvil */}
                      <div style={{ display: 'flex', gap: '2px' }}>
                        <button
                          type="button"
                          onClick={() => handleMoveRep(idx, 'up')}
                          disabled={idx === 0}
                          className="btn-icon"
                          style={{ padding: '4px', opacity: idx === 0 ? 0.3 : 1 }}
                          title="Subir orden"
                          aria-label="Subir orden"
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveRep(idx, 'down')}
                          disabled={idx === reps.length - 1}
                          className="btn-icon"
                          style={{ padding: '4px', opacity: idx === reps.length - 1 ? 0.3 : 1 }}
                          title="Bajar orden"
                          aria-label="Bajar orden"
                        >
                          <ArrowDown size={14} />
                        </button>
                      </div>

                      {/* ATAQUE: Quinteto y Jugada */}
                      <div
                        style={{
                          flex: '1 1 300px',
                          backgroundColor: 'rgba(217, 35, 44, 0.04)',
                          padding: '0.45rem 0.65rem',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid rgba(217, 35, 44, 0.15)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.35rem',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--brand-red)', textTransform: 'uppercase' }}>
                            Ataque
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setLineupModalState({
                                isOpen: true,
                                repIndex: idx,
                                unit: 'offense',
                                initialName: rep.offense_lineup_name,
                                initialSlots: rep.offense_slots,
                                sessionKey: Date.now(),
                              })
                            }
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--brand-red)',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.2rem',
                              padding: 0,
                            }}
                          >
                            <Edit3 size={12} /> {rep.offense_lineup_is_custom ? 'Personalizado (Editar)' : 'Ajustar 5'}
                          </button>
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <select
                            value={rep.offense_lineup_template_id || ''}
                            onChange={(e) => handleSelectLineupTemplate(idx, 'offense', e.target.value)}
                            style={{ flex: '1 1 120px', padding: '0.3rem 0.45rem', fontSize: '0.8rem', borderRadius: 'var(--radius-sm)' }}
                          >
                            <option value="">{rep.offense_lineup_is_custom ? `[Modificado] ${rep.offense_lineup_name}` : 'Seleccionar plantilla'}</option>
                            {lineupTemplates
                              .filter((t) => t.unit === 'offense')
                              .map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                          </select>

                          <select
                            value={rep.offense_play_id || (rep.offense_play_code === 'LIBRE' ? 'LIBRE' : '')}
                            onChange={(e) => handleSelectOffensePlay(idx, e.target.value)}
                            style={{ flex: '1 1 150px', padding: '0.3rem 0.45rem', fontSize: '0.8rem', borderRadius: 'var(--radius-sm)' }}
                          >
                            <option value="LIBRE">-- Libre / no prefijada --</option>
                            {offensivePlays.map((p) => (
                              <option key={p.id} value={p.id}>
                                [{p.short_code}] {p.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* DEFENSIVE: Quinteto y Llamada */}
                      <div
                        style={{
                          flex: '1 1 300px',
                          backgroundColor: 'rgba(37, 99, 235, 0.04)',
                          padding: '0.45rem 0.65rem',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid rgba(37, 99, 235, 0.15)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.35rem',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase' }}>
                            Defensa
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setLineupModalState({
                                isOpen: true,
                                repIndex: idx,
                                unit: 'defense',
                                initialName: rep.defense_lineup_name,
                                initialSlots: rep.defense_slots,
                                sessionKey: Date.now(),
                              })
                            }
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#2563eb',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.2rem',
                              padding: 0,
                            }}
                          >
                            <Edit3 size={12} /> {rep.defense_lineup_is_custom ? 'Personalizado (Editar)' : 'Ajustar 5'}
                          </button>
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <select
                            value={rep.defense_lineup_template_id || ''}
                            onChange={(e) => handleSelectLineupTemplate(idx, 'defense', e.target.value)}
                            style={{ flex: '1 1 120px', padding: '0.3rem 0.45rem', fontSize: '0.8rem', borderRadius: 'var(--radius-sm)' }}
                          >
                            <option value="">{rep.defense_lineup_is_custom ? `[Modificado] ${rep.defense_lineup_name}` : 'Seleccionar plantilla'}</option>
                            {lineupTemplates
                              .filter((t) => t.unit === 'defense')
                              .map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                          </select>

                          <select
                            value={rep.defense_call_id || (rep.defense_call_code === 'LIBRE' ? 'LIBRE' : '')}
                            onChange={(e) => handleSelectDefenseCall(idx, e.target.value)}
                            style={{ flex: '1 1 150px', padding: '0.3rem 0.45rem', fontSize: '0.8rem', borderRadius: 'var(--radius-sm)' }}
                          >
                            <option value="LIBRE">-- Libre / no prefijada --</option>
                            {defensiveCalls.map((c) => (
                              <option key={c.id} value={c.id}>
                                [{c.short_code}] {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Acciones de fila */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginLeft: 'auto' }}>
                        <button
                          type="button"
                          onClick={() => handleDuplicateRep(idx)}
                          className="btn-icon"
                          title="Duplicar repetición"
                          aria-label="Duplicar repetición"
                        >
                          <Copy size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteRep(idx)}
                          className="btn-icon"
                          style={{ color: 'var(--brand-red)' }}
                          title="Eliminar repetición"
                          aria-label="Eliminar repetición"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>

                    {/* Alerta de Conflicto en esta rep */}
                    {hasRepConflict && (
                      <div
                        style={{
                          padding: '0.35rem 0.65rem',
                          backgroundColor: 'rgba(217, 35, 44, 0.08)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.75rem',
                          color: 'var(--brand-red)',
                          fontWeight: 600,
                        }}
                      >
                        ⚠️ Conflicto: Hay jugadores figurando a la vez en el quinteto de ataque y de defensa de esta repetición.
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

      </Modal>

      {/* Submodal para personalizar quinteto de una repetición */}
      {lineupModalState.isOpen && (
        <LineupEditorModal
          key={`lineup-rep-${lineupModalState.unit}-${lineupModalState.repIndex}-${lineupModalState.sessionKey || 0}`}
          isOpen={lineupModalState.isOpen}
          onClose={() =>
            setLineupModalState((prev) => ({ ...prev, isOpen: false }))
          }
          unit={lineupModalState.unit}
          teamId={teamId}
          initialName={lineupModalState.initialName}
          initialSlots={lineupModalState.initialSlots}
          athletes={rosterAthletes}
          isCustomLineupMode={true}
          onSaveTemplate={async (newTemplateName, newSlots) => {
            await sportsService.saveLineupTemplate({
              teamId,
              unit: lineupModalState.unit,
              name: newTemplateName,
              slots: newSlots,
            })
            // Refrescar lista de plantillas
            const templates = await sportsService.getLineupTemplates(teamId, { status: 'active' })
            setLineupTemplates(templates)
          }}
          onApplyCustomLineup={(customName, customSlots) => {
            const idx = lineupModalState.repIndex
            if (idx >= 0 && idx < reps.length) {
              const updated = [...reps]
              if (lineupModalState.unit === 'offense') {
                updated[idx] = {
                  ...updated[idx],
                  offense_lineup_name: customName,
                  offense_lineup_is_custom: true,
                  offense_slots: customSlots,
                }
              } else {
                updated[idx] = {
                  ...updated[idx],
                  defense_lineup_name: customName,
                  defense_lineup_is_custom: true,
                  defense_slots: customSlots,
                }
              }
              setReps(updated)
            }
          }}
        />
      )}
    </>
  )
}
